import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Wrench } from "lucide-react";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer, toast } from "react-toastify";
import useSkills from "hooks/useSkills";
import { useMutation, useQueryClient } from "react-query";
import { addSkill, deleteSkill, editSkill } from "services/general.service";
import { Skill, SkillCreate } from "types/general.interface";
import SkillForm from "./SkillForm";

const Skills = () => {
  const { data, isLoading } = useSkills("");
  const [record, setRecord] = useState<Skill | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const queryClient = useQueryClient();

  const { isLoading: adding, mutate } = useMutation(
    async (input: SkillCreate) => await addSkill(input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        setShowAdd(false);
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const { isLoading: deleting, mutate: deleteMutate } = useMutation(
    async () => await deleteSkill(record?._id!),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        setShowDelete(false);
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const { isLoading: editing, mutate: editMutate } = useMutation(
    async (input: SkillCreate) => await editSkill(record?._id!, input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        setShowAdd(false);
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const handleSubmit = (data: SkillCreate) => {
    if (record) {
      editMutate(data);
    } else {
      mutate(data);
    }
  };

  const onEdit = (item: Skill) => {
    setRecord(item);
    setShowAdd(true);
  };

  const handleAdd = () => {
    setRecord(null);
    setShowAdd(true);
  };

  return (
    <div className="p-6">
      <BreadCrumb title="Skills" pageTitle="Admin" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">All Skills</h2>
          <button
            onClick={handleAdd}
            type="button"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Plus className="size-4" />
            Add Skill
          </button>
        </div>

        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500">Loading…</td>
                </tr>
              ) : !data?.data?.length ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Wrench className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No skills found</p>
                  </td>
                </tr>
              ) : (
                data.data.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Name" data-cell="title" className="px-4 py-3 text-gray-900">{item.name}</td>
                    <td data-label="Category" className="px-4 py-3 text-gray-500">{item.serviceCategory || "—"}</td>
                    <td data-label="Actions" data-cell="actions" className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => { setRecord(item); setShowDelete(true); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        id="skillModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-base font-semibold text-gray-900">
            {record ? "Edit Skill" : "Add Skill"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-5 overflow-y-auto">
          <SkillForm
            onCancel={() => setShowAdd(false)}
            onSubmit={handleSubmit}
            isLoading={adding || editing}
            data={record}
          />
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onDelete={() => deleteMutate()}
      />
    </div>
  );
};

export default Skills;
